/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ConferenceProfileFormPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Wrapper page component for Conference Profile Form that integrates with React Router.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ConferenceProfileFormView from './ConferenceProfileFormView';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { fetchConferenceProfile, createConferenceProfile, updateConferenceProfile, deleteConferenceProfile } from '../conferenceProfiles/api';
import { ConferenceProfile } from '../conferenceProfiles/types';

export default function ConferenceProfileFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showInfo } = useNotifications();
  
  const [profile, setProfile] = useState<ConferenceProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const duplicateId = searchParams.get('duplicate');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  
  // Refs for stable function references to prevent infinite loops
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  
  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
    tRef.current = t;
  }, [showError, showInfo, t]);

  const loadProfile = useCallback(async (profileId: string, isDuplicate: boolean = false) => {
    if (!profileId) {
      setLoading(false);
      isLoadingRef.current = false;
      return;
    }

    // Prevent loading the same ID multiple times
    if (lastLoadedIdRef.current === profileId && !isDuplicate) {
      setLoading(false);
      isLoadingRef.current = false;
      return;
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      return;
    }

    try {
      setLoading(true);
      isLoadingRef.current = true;
      lastLoadedIdRef.current = profileId;

      const result = await fetchConferenceProfile(profileId);
      const loadedProfile = result.data;
      
      if (isDuplicate) {
        // Clear ID and UUID for duplicate, modify name
        loadedProfile.id = undefined as any;
        loadedProfile.uuid = undefined as any;
        loadedProfile.name = `${loadedProfile.name}_copy`;
        if (loadedProfile.description) {
          loadedProfile.description = `${loadedProfile.description} (Copy)`;
        }
        showInfoRef.current(tRef.current('conferenceProfiles:messages.duplicateHint'));
      }
      
      setProfile(loadedProfile);
    } catch (error) {
      lastLoadedIdRef.current = null;
      showErrorRef.current(error?.message || tRef.current('conferenceProfiles:messages.loadError'));
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  // Store stable reference to avoid re-running the fetch effect
  const loadProfileRef = useRef(loadProfile);
  
  // Update ref when value changes (but don't trigger effects)
  useEffect(() => {
    loadProfileRef.current = loadProfile;
  }, [loadProfile]);

  // Load profile data if editing or duplicating
  useEffect(() => {
    if (mode === 'edit' && id) {
      loadProfileRef.current(id, false);
    } else if (duplicateId) {
      loadProfileRef.current(duplicateId, true);
    } else {
      setProfile(null);
      lastLoadedIdRef.current = null;
    }
  }, [id, mode, duplicateId]);

  // Reset load guard on unmount - ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = async (profileData: any) => {
    let result;
    
    if (mode === 'edit' && id) {
      result = await updateConferenceProfile(id, profileData);
      // Reload the profile data after update
      await loadProfile(id, false);
    } else {
      result = await createConferenceProfile(profileData);
      // Navigation is now handled by useSaveWithAction in the form view
    }

    return result;
  };

  const handleBack = () => {
    navigate('/settings/pbx/conference-profiles');
  };

  const handleDelete = async () => {
    if (!id || id === 'new') return;

    try {
      await deleteConferenceProfile(id);
      // Navigate to list after successful delete
      navigate('/settings/pbx/conference-profiles');
    } catch (error) {
      throw error; // Re-throw to let the form view handle error display
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <ConferenceProfileFormView
      onBack={handleBack}
      onSave={handleSave}
      onDelete={mode === 'edit' ? handleDelete : undefined}
      profile={profile}
      mode={mode}
    />
  );
}
